import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

const IN = 0.0254;

const LIP_H = 0.4 * IN;
const FOOT_H = 1.25 * IN;
const FOOT_W = 3 * IN;
const FOOT_INSET = 1.5 * IN;
const FRAME = 0.6 * IN;
const DIVIDER_H = 2.5 * IN;
const SLAT_H = 0.3 * IN;
const SLAT_GAP = 0.45 * IN;
const KNOB_R = 0.45 * IN;

function slatPositions(regionTop, regionH) {
  const step = SLAT_H + SLAT_GAP;
  const count = Math.max(2, Math.floor((regionH + SLAT_GAP) / step));
  const totalH = count * SLAT_H + (count - 1) * SLAT_GAP;
  const startY = regionTop + (regionH - totalH) / 2 + SLAT_H / 2;
  return Array.from({ length: count }, (_, i) => startY + i * step);
}

export function DormUnitVentilatorSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <UnitVentSide
          depth={depth}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      </SchematicShell>
    );
  }

  const lipBottom = LIP_H;
  const footTop = height - FOOT_H;
  const grilleAreaTop = lipBottom + FRAME;
  const grilleAreaBottom = footTop - FRAME;
  const totalGrilleH = grilleAreaBottom - grilleAreaTop - DIVIDER_H;
  const upperH = totalGrilleH * 0.38;
  const lowerH = totalGrilleH * 0.62;
  const grilleLeft = FRAME;
  const grilleRight = width - FRAME;

  const upperTop = grilleAreaTop;
  const upperBottom = upperTop + upperH;
  const dividerTop = upperBottom;
  const dividerBottom = dividerTop + DIVIDER_H;
  const lowerTop = dividerBottom;
  const lowerBottom = lowerTop + lowerH;

  const upperSlats = slatPositions(upperTop, upperH);
  const lowerSlats = slatPositions(lowerTop, lowerH);

  const dividerMidY = (dividerTop + dividerBottom) / 2;
  const knob1X = width - FRAME - KNOB_R * 1.2;
  const knob2X = knob1X - KNOB_R * 3;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Top lip */}
      <Line
        x1={0}
        y1={lipBottom}
        x2={width}
        y2={lipBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Upper grille border */}
      <Line
        x1={grilleLeft}
        y1={upperTop}
        x2={grilleRight}
        y2={upperTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={grilleLeft}
        y1={upperTop}
        x2={grilleLeft}
        y2={upperBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={grilleRight}
        y1={upperTop}
        x2={grilleRight}
        y2={upperBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Upper slats */}
      {upperSlats.map((y, i) => (
        <Line
          key={`u-${i}`}
          x1={grilleLeft}
          y1={y}
          x2={grilleRight}
          y2={y}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth * 0.5}
        />
      ))}

      {/* Divider strip (full-width lines) */}
      <Line
        x1={0}
        y1={dividerTop}
        x2={width}
        y2={dividerTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={0}
        y1={dividerBottom}
        x2={width}
        y2={dividerBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Lower grille border */}
      <Line
        x1={grilleLeft}
        y1={lowerBottom}
        x2={grilleRight}
        y2={lowerBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={grilleLeft}
        y1={lowerTop}
        x2={grilleLeft}
        y2={lowerBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={grilleRight}
        y1={lowerTop}
        x2={grilleRight}
        y2={lowerBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Lower slats */}
      {lowerSlats.map((y, i) => (
        <Line
          key={`l-${i}`}
          x1={grilleLeft}
          y1={y}
          x2={grilleRight}
          y2={y}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth * 0.5}
        />
      ))}

      {/* Foot line */}
      <Line
        x1={0}
        y1={footTop}
        x2={width}
        y2={footTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Foot inner edges */}
      <Line
        x1={FOOT_INSET + FOOT_W}
        y1={footTop}
        x2={FOOT_INSET + FOOT_W}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={width - FOOT_INSET - FOOT_W}
        y1={footTop}
        x2={width - FOOT_INSET - FOOT_W}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Control knobs */}
      <circle
        cx={knob1X}
        cy={dividerMidY}
        r={KNOB_R}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <circle
        cx={knob2X}
        cy={dividerMidY}
        r={KNOB_R * 0.7}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </SchematicShell>
  );
}

function UnitVentSide({ depth, height, strokeColor, strokeWidth }) {
  return (
    <g>
      <Line
        x1={0}
        y1={LIP_H}
        x2={depth}
        y2={LIP_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={0}
        y1={height - FOOT_H}
        x2={depth}
        y2={height - FOOT_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

DormUnitVentilatorSchematic.supportsSideViews = true;
